import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../../App.styles';
import { BopisData } from '../../../../../../hooks';
import { useCurrencyTools } from '../../../../../../hooks/useCurrencyTools';
import { Icon, Icons, Tooltip, Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../../theme';
import { homeTranslations } from '../../../../../../translations';
import { formatValue } from '../../../../../../utils';
import { MetricItemsContainer, MetricItemWrapper, StyledValueTypography } from './OverviewContent.styles';

const MetricItem = ({
  title,
  valueText,
  valueSymbolText,
  tooltipText,
  icon,
}: {
  title: string;
  valueText: string;
  valueSymbolText: string | null;
  tooltipText: string;
  icon?: Icons;
}) => {
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  return (
    <MetricItemWrapper
      justifyContent="center"
      alignItems="center"
      flex={1}
      gap={[4]}
      alignSelf="center"
      position="relative"
    >
      {icon && <Icon icon={icon} size={isMobile ? 'medium' : 'large'} margin={[0]} />}

      <ColumnFlexContainer>
        <Typography>{title}</Typography>
        <RowFlexContainer gap={[1]} alignItems="baseline">
          <StyledValueTypography type="h2">{valueText}</StyledValueTypography>
          <SecondaryTextTypography>{valueSymbolText}</SecondaryTextTypography>
        </RowFlexContainer>
      </ColumnFlexContainer>
      <ColumnFlexContainer position="absolute" top="-20px" right="8px">
        <Tooltip
          text={
            <Typography type="caption" color="white">
              {tooltipText}
            </Typography>
          }
          position="top"
          arrowedToolTip
        >
          <Icon icon={Icons.INFO} size="small" />
        </Tooltip>
      </ColumnFlexContainer>
    </MetricItemWrapper>
  );
};

export type OverviewContentProps = {
  bopisData: BopisData;
};

export const OverviewContent: FC<OverviewContentProps> = ({ bopisData }) => {
  const { t } = useTranslation();
  const { selectedCurrency } = useCurrencyTools();
  const { bopis: bopisTranslations } = homeTranslations;

  const { bopisNetSales, totalNetSales, upsellingValue, convertedLoyaltyCustomers, totalLoyaltyCustomers } =
    bopisData;

  const shareOfBusiness = totalNetSales === 0 ? 0 : (bopisNetSales / totalNetSales) * 100;
  const conversionRate =
    totalLoyaltyCustomers === 0 ? 0 : (convertedLoyaltyCustomers / totalLoyaltyCustomers) * 100;

  return (
    <ColumnFlexContainer flex={1} gap={[2]} padding={[4, 4]} data-testid="overviewContent">
      <MetricItemsContainer>
        <MetricItem
          title={t(bopisTranslations.overviewBopisSales.key, bopisTranslations.overviewBopisSales.defaultValue)}
          valueText={formatValue(bopisNetSales, {
            options: { minimumFractionDigits: 0, notation: 'compact' },
          }).toUpperCase()}
          valueSymbolText={selectedCurrency}
          icon={Icons.BAG}
          tooltipText={t(
            bopisTranslations.bopisSalesTooltipText.key,
            bopisTranslations.bopisSalesTooltipText.defaultValue,
          )}
        />

        <MetricItem
          title={t(
            bopisTranslations.overviewShareOfBusiness.key,
            bopisTranslations.overviewShareOfBusiness.defaultValue,
          )}
          valueText={shareOfBusiness.toFixed(2)}
          valueSymbolText="%"
          tooltipText={t(
            bopisTranslations.shareOfBusinessTooltipText.key,
            bopisTranslations.shareOfBusinessTooltipText.defaultValue,
          )}
        />
      </MetricItemsContainer>

      <MetricItemsContainer>
        <MetricItem
          title={t(
            bopisTranslations.overviewUpsellingAmount.key,
            bopisTranslations.overviewUpsellingAmount.defaultValue,
          )}
          valueText={formatValue(upsellingValue, {
            options: { minimumFractionDigits: 0, notation: 'compact' },
          }).toUpperCase()}
          valueSymbolText={selectedCurrency}
          icon={Icons.SALESEFFICIENCY}
          tooltipText={t(
            bopisTranslations.upsellingAmountTooltipText.key,
            bopisTranslations.upsellingAmountTooltipText.defaultValue,
          )}
        />

        <MetricItem
          title={t(
            bopisTranslations.overviewConversionRate.key,
            bopisTranslations.overviewConversionRate.defaultValue,
          )}
          valueText={conversionRate.toFixed(2)}
          valueSymbolText="%"
          tooltipText={t(
            bopisTranslations.conversionRateTooltipText.key,
            bopisTranslations.conversionRateTooltipText.defaultValue,
          )}
        />
      </MetricItemsContainer>
    </ColumnFlexContainer>
  );
};
